import { getIndexDBLoaderWebWorker, getLoadStoreRequestMessage } from 'helpers/StoreHelpers';
import { GameWeek, IWorkerIDBLoaderResponseMessage, SeasonID, Stores } from 'types';

/** A result means the interface finished its cache writes; worker failures reject the load. */
export const loadStoreFromWorker = <T>(
  store: Stores, seasonID: SeasonID, shouldFetch: boolean, signal: AbortSignal,
  weekID?: GameWeek, modelVersion?: string,
): Promise<T> => new Promise((resolve, reject) => {
  if (signal.aborted) { reject(new Error('Load cancelled')); return; }
  const worker = getIndexDBLoaderWebWorker(`Load-${store}-${seasonID}`);
  const cleanup = () => {
    signal.removeEventListener('abort', cancel);
    worker.terminate();
  };
  const fail = (error: unknown) => { cleanup(); reject(error); };
  const cancel = () => fail(new Error('Load cancelled'));
  signal.addEventListener('abort', cancel, { once: true });
  worker.onmessage = (event: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
    if (event.data.type === 'error-get-store-data') {
      fail(new Error(String(event.data.payload)));
    } else if (event.data.type === 'result-get-store-data') {
      try {
        const data = JSON.parse(event.data.payload) as T;
        cleanup();
        resolve(data);
      } catch (error) { fail(error); }
    }
  };
  worker.onerror = () => fail(new Error(`Failed to load ${store} for ${seasonID}`));
  worker.onmessageerror = () => fail(new Error(`Invalid worker response for ${store}`));
  try { worker.postMessage(getLoadStoreRequestMessage(store, seasonID, shouldFetch, weekID, modelVersion)); }
  catch (error) { fail(error); }
});
